import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useWmgSession } from '~/modules/auth/hooks/useWmgSession';

import { DEFAULT_DROPDOWN_PAGE_SIZE_NO_PAGINATION } from '../../constants';
import { PresentationLabelQueryKeys } from '../config/queryKeys';
import { getRequestHeaders } from '../lib/headers';
import { LegacyCoopClient } from '../lib/LegacyCoopClient';
import type { PresentationLabelListResponse } from '../lib/schemas';
import { PresentationLabelListResponseSchema } from '../lib/schemas';
import type { PresetUseQueryOptions } from '../lib/types';
import type { PaginationVariables } from '../types/paginationVariables';
import type { SearchVariables } from '../types/searchVariables';

export interface PresentationLabelListQueryVariables
  extends PaginationVariables,
    SearchVariables {}

export function usePresentationLabelList<TData = PresentationLabelListResponse>(
  variables: PresentationLabelListQueryVariables,
  options?: PresetUseQueryOptions<PresentationLabelListResponse, TData>,
) {
  const session = useWmgSession();

  const { data, ...query } = useQuery({
    queryKey: PresentationLabelQueryKeys.list(variables),
    queryFn: async () => {
      const responseData = await LegacyCoopClient.get('labels/presentation', {
        headers: getRequestHeaders(session.token),
        searchParams: { ...variables },
      }).json<PresentationLabelListResponse>();

      return PresentationLabelListResponseSchema.parse({
        data: responseData.data,
        total: responseData.total,
        page: 1,
        totalPages: Math.ceil(
          responseData.total /
            (variables.take ?? DEFAULT_DROPDOWN_PAGE_SIZE_NO_PAGINATION),
        ),
      });
    },
    placeholderData: keepPreviousData,
    ...options,
  });

  return { presentationLabelList: data, ...query };
}
